import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";
import type { KycRecord } from "@/types/kyc";
import { formatINR } from "@/pages/admin/data";
import { KYC_FEE } from "@/pages/retailer/wallet/constants";

const REJECT_REASONS = [
  { value: "Documents Not Readable", label: "Documents Not Readable" },
  { value: "Details Mismatch", label: "Details Mismatch" },
  { value: "Invalid Business Proof", label: "Invalid Business Proof" },
  { value: "Suspected Fraudulent Documents", label: "Suspected Fraudulent Documents" },
  { value: "Incomplete Application", label: "Incomplete Application" },
  { value: "Other", label: "Other" },
];

interface RejectModalProps {
  open: boolean;
  onClose: () => void;
  record: KycRecord | null;
  onConfirm: (reason: string, remarks: string) => void;
}

export default function RejectModal({ open, onClose, record, onConfirm }: RejectModalProps) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function reset() {
    setReason("");
    setRemarks("");
    setConfirmed(false);
    setSubmitting(false);
    setError("");
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleConfirm() {
    if (!reason) {
      setError("Please select a reason");
      return;
    }
    if (!remarks.trim()) {
      setError("Please add remarks for the retailer");
      return;
    }
    setError("");
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onConfirm(reason, remarks.trim());
      reset();
    }, 900);
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Reject this KYC application?"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={submitting} disabled={!confirmed}>
            Reject KYC
          </Button>
        </div>
      }
    >
      {record && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] p-4">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[#DC2626]">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <p className="text-[13px] text-ink-secondary">
              This action is permanent. The retailer will not be able to resubmit, and the {formatINR(KYC_FEE)} fee
              must be refunded manually through a wallet adjustment.
            </p>
          </div>

          <Select
            label="Reason"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={REJECT_REASONS}
            placeholder="Select a reason"
            error={error && !reason ? error : undefined}
          />

          <Textarea
            label="Remarks for Retailer"
            required
            rows={3}
            maxLength={200}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            helper="This message will be shown to the retailer"
            placeholder="Explain why the application was rejected"
            error={error && !remarks.trim() ? error : undefined}
          />
          <p className="-mt-3 text-right text-xs text-ink-muted">{remarks.length}/200</p>

          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label="I confirm this application should be permanently rejected."
          />
          {error && !reason && !remarks.trim() && <p className="text-[13px] text-danger">{error}</p>}
        </div>
      )}
    </Modal>
  );
}